import { Box, Button, Input } from "@chakra-ui/react";
import { useState } from "react";
import api from "../../../lib/api";
import { toaster } from "../../../components/ui/toaster";
import { useAppSelector } from "../../../store/hooks";

export default function CreateUserModal({
  onClose,
  onUserCreated,
}: {
  onClose: () => void;
  onUserCreated: () => void;
}) {
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === "admin";

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "user",
  });
  const [loading, setLoading] = useState(false);

  const allowedRoles = isAdmin ? ["user", "admin"] : ["user"];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.role
    ) {
      toaster.create({
        title: "Validation Error",
        description: "Please fill all fields",
        type: "error",
      });
      return;
    }

    setLoading(true);

    try {
      if (!allowedRoles.includes(formData.role)) {
        throw new Error("You are not allowed to assign this role");
      }

      const response = await api.post("/auth/invite", {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        role: formData.role,
      });

      toaster.create({
        title: "Success",
        description: response.data.message || "User invited successfully",
        type: "success",
      });

      onUserCreated();
    } catch (error: any) {
      console.error("Invite user error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "Failed to invite user";

      toaster.create({
        title: "Error",
        description: errorMessage,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex={50}
      onClick={onClose}>
      <Box
        bg="white"
        borderRadius="xl"
        shadow="xl"
        p={8}
        w="full"
        maxW="md"
        onClick={(e) => e.stopPropagation()}>
        <Box fontSize="2xl" fontWeight="bold" color="gray.800" mb={6}>
          Invite New User
        </Box>

        <Box display="flex" flexDirection="column" gap={5}>
          <Box>
            <Box fontSize="14px" fontWeight="500" color="gray.700" mb={2}>
              First Name
            </Box>
            <Input
              value={formData.firstName}
              onChange={(e) =>
                setFormData({ ...formData, firstName: e.target.value })
              }
              placeholder="Enter first name"
              size="lg"
              borderColor="gray.300"
              disabled={loading}
            />
          </Box>

          <Box>
            <Box fontSize="14px" fontWeight="500" color="gray.700" mb={2}>
              Last Name
            </Box>
            <Input
              value={formData.lastName}
              onChange={(e) =>
                setFormData({ ...formData, lastName: e.target.value })
              }
              placeholder="Enter last name"
              size="lg"
              borderColor="gray.300"
              disabled={loading}
            />
          </Box>

          <Box>
            <Box fontSize="14px" fontWeight="500" color="gray.700" mb={2}>
              Email Address
            </Box>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              placeholder="Enter email"
              size="lg"
              borderColor="gray.300"
              disabled={loading}
            />
          </Box>

          <Box>
            <Box fontSize="14px" fontWeight="500" color="gray.700" mb={2}>
              Assign Role
            </Box>
            <select
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
              disabled={loading}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #D1D5DB",
                borderRadius: "8px",
                outline: "none",
                backgroundColor: "white",
                fontSize: "16px",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
              }}>
              <option value="user">User</option>
              {isAdmin && <option value="admin">Administrator</option>}
            </select>
          </Box>

          <Box display="flex" gap={3} pt={4}>
            <Button
              onClick={handleSubmit}
              colorScheme="blue"
              flex={1}
              loading={loading}
              disabled={loading}>
              Invite User
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              flex={1}
              disabled={loading}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
